/**
 * ModelPicker — IDE-style preset selector for a single card.
 *
 * Notion / Linear conventions (UI_GUIDELINES_v1.md):
 *   - Compact closed state: provider name + model + tier label, no
 *     coloured badge (ADR-057 — chrome stays monochrome). A small dot in
 *     the muted-foreground colour is the only visual signal.
 *   - On open, a popover appears with the global preset catalogue grouped
 *     by provider. Each row shows tier + thinking + research toggles
 *     compactly. Tier / thinking / research badges are text-only.
 *   - Selection is persisted via PUT /api/routing/preset/:cardId.
 *
 * The picker doesn't expose granular toggles (temperature, max tokens) —
 * those live in dedicated Advanced flyouts in Phase 6 (Architect Agent
 * UI). The preset catalogue is the canonical surface today.
 */
import type { EffortTier, ModelChoice, ModelPreset } from '@chaos/types/agent';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { agentApi, agentKeys } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface ModelPickerProps {
  cardId: string;
  className?: string;
}

const TIER_LABELS: Record<EffortTier, string> = {
  fast: 'Fast',
  balanced: 'Balanced',
  deep: 'Deep',
};

const PROVIDER_LABELS: Record<ModelChoice['provider'], string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  xai: 'xAI',
};

/**
 * Group presets by provider + sort by tier so the catalogue reads in
 * the same order the founder thinks about them.
 */
function groupByProvider(presets: ModelPreset[]): Array<{
  provider: ModelChoice['provider'];
  rows: ModelPreset[];
}> {
  const map = new Map<ModelChoice['provider'], ModelPreset[]>();
  for (const p of presets) {
    const arr = map.get(p.modelChoice.provider) ?? [];
    arr.push(p);
    map.set(p.modelChoice.provider, arr);
  }
  const tierOrder: Record<EffortTier, number> = { fast: 0, balanced: 1, deep: 2 };
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const t = tierOrder[a.modelChoice.tier] - tierOrder[b.modelChoice.tier];
      if (t !== 0) return t;
      return a.name.localeCompare(b.name);
    });
  }
  const providerOrder: Record<ModelChoice['provider'], number> = {
    anthropic: 0,
    openai: 1,
    google: 2,
    xai: 3,
  };
  return Array.from(map.entries())
    .sort(([a], [b]) => providerOrder[a] - providerOrder[b])
    .map(([provider, rows]) => ({ provider, rows }));
}

export function ModelPicker({ cardId, className }: ModelPickerProps) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const presetsQ = useQuery({
    queryKey: agentKeys.presets(),
    queryFn: () => agentApi.listPresets(),
    staleTime: 5 * 60_000,
  });

  const cardPresetQ = useQuery({
    queryKey: agentKeys.cardPreset(cardId),
    queryFn: () => agentApi.getCardPreset(cardId),
  });

  const setM = useMutation({
    mutationFn: (presetId: string | null) => agentApi.setCardPreset(cardId, presetId),
    onSuccess: (state) => {
      qc.setQueryData(agentKeys.cardPreset(cardId), state);
    },
  });

  const presets = presetsQ.data ?? [];
  const grouped = React.useMemo(() => groupByProvider(presets), [presets]);
  const activePresetId = cardPresetQ.data?.presetId ?? null;
  const effective = cardPresetQ.data?.effective;

  const label = effective
    ? `${PROVIDER_LABELS[effective.provider]} · ${effective.model}`
    : 'Loading…';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select model"
          className={cn(
            'group inline-flex h-7 w-full items-center justify-between gap-2 rounded',
            'border border-border bg-background px-2 text-left text-[12px] font-medium text-foreground',
            'transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              aria-hidden
              className="h-1.5 w-1.5 rounded-full bg-foreground/40 transition-opacity group-hover:bg-foreground/60"
            />
            <span className="truncate">{label}</span>
            {effective && (
              <span className="text-muted-foreground">· {TIER_LABELS[effective.tier]}</span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-80 max-w-[88vw] p-0">
        <div className="max-h-96 overflow-y-auto py-1">
          {presetsQ.isLoading && (
            <div className="px-3 py-2 text-[12px] text-muted-foreground">Loading presets…</div>
          )}
          {presetsQ.isError && (
            <div className="px-3 py-2 text-[12px] text-error">Could not load presets.</div>
          )}
          {!presetsQ.isLoading && presets.length === 0 && (
            <div className="px-3 py-2 text-[12px] text-muted-foreground">
              No presets configured.
            </div>
          )}

          {grouped.map(({ provider, rows }) => (
            <section key={provider} className="py-1">
              <div className="px-3 pb-0.5 pt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {PROVIDER_LABELS[provider]}
              </div>
              {rows.map((preset) => {
                const isActive = preset.id === activePresetId;
                const isEffective =
                  !activePresetId &&
                  effective?.provider === preset.modelChoice.provider &&
                  effective?.model === preset.modelChoice.model &&
                  effective?.tier === preset.modelChoice.tier;
                const indicate = isActive || isEffective;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      setM.mutate(preset.id);
                      setOpen(false);
                    }}
                    disabled={setM.isPending}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-1.5 text-left text-[12px]',
                      'transition-colors hover:bg-muted/60',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                  >
                    <span className="mt-0.5 inline-flex w-4 shrink-0 justify-center">
                      {indicate && <Check className="h-3.5 w-3.5 text-foreground" aria-hidden />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-1.5">
                        <span className="font-medium text-foreground">{preset.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {TIER_LABELS[preset.modelChoice.tier]}
                        </span>
                        {preset.modelChoice.thinking !== 'off' && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            · Thinking
                          </span>
                        )}
                        {preset.modelChoice.researchMode && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            · Research
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {preset.modelChoice.model}
                        {preset.description ? ` — ${preset.description}` : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </section>
          ))}

          {activePresetId && (
            <>
              <div className="my-1 border-t border-border" />
              <button
                type="button"
                onClick={() => {
                  setM.mutate(null);
                  setOpen(false);
                }}
                disabled={setM.isPending}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-muted-foreground',
                  'transition-colors hover:bg-muted/60 hover:text-foreground',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                <span className="inline-flex w-4 shrink-0" />
                <span>Reset to column default</span>
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
